import React, { useEffect } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import {
  AlignItems,
  withCitrusTheme,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'
import useSWR from 'swr'

import { fetchUserPromoRules } from '@library/api-user'

import { DiscountPerWeek } from './components/DiscountPerWeek'

type PromoRule = {
  campaignId: string
}

type PromoRulesResponse = PromoRule[]

const ProgressBarContainer = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.ColdGrey_500),
  width: '100%',
  height: '8px',
  borderRadius: '4px',
  marginBottom: '16px',
}))
const ProgressBar = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Primary_600),
  width: '50%',
  height: '100%',
  borderRadius: '4px',
  animation: 'progress 2s linear',
  '@keyframes progress': {
    '0%': { width: '0%' },
    '100%': { width: '50%' },
  },
}))
const Circle = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Primary_600),
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  position: 'relative',
  top: '-11px',
  left: '12%',
}))
const CircleTwo = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.Primary_600),
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  position: 'relative',
  top: '-27px',
  left: 'calc(50% - 8px)',
}))
const CircleThree = withCitrusTheme(Box, (theme) => ({
  backgroundColor: theme.color(Color.ColdGrey_500),
  border: `2px solid ${theme.color(Color.ColdGrey_300)}`,
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  position: 'relative',
  top: '-44px',
  left: 'calc(85% - 8px)',
}))
const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 8,
}))

export function DiscountBanner({ fallbackCampaignId }: { fallbackCampaignId: string }) {
  const campaignIdDiscountsMap: Record<string, string[]> = {
    '8956': ['50%', '40%', '20%'],
    '1523': ['65%', '40%', '20%'],
    '9560': ['70%', '40%', '25%'],
  }

  const campaignIdKeys = Object.keys(campaignIdDiscountsMap)

  const { data, error } = useSWR('/user/current/promo', fetchUserPromoRules)

  let campaignId = (data as PromoRulesResponse)?.[0]?.campaignId

  if (!campaignId || error) {
    campaignId = fallbackCampaignId
  }

  useEffect(() => {
    datadogLogs.logger.info('CampaignId for discount per week', { campaignId })
  }, [campaignId])

  if (!campaignIdKeys.includes(campaignId) || fallbackCampaignId === '') {
    return null
  }

  return (
    <RoundedBox
      data-testid="discount-banner"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      borderRadius={1.5}
      bg={Color.ColdGrey_800}
      color={Color.White}
      aria-label="Apply welcome offer discount"
      paddingV={4}
      paddingH={3}
      tabIndex={0}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        flexDirection={FlexDirection.Column}
        gap={2}
      >
        <Text fontWeight={FontWeight.Bold} size={2} color={Color.White}>
          Your Welcome Offer
        </Text>
        <ProgressBarContainer>
          <ProgressBar />
          <Circle />
          <CircleTwo />
          <CircleThree />
        </ProgressBarContainer>
        <DiscountPerWeek campaignId={campaignId} campaignIdDiscountsMap={campaignIdDiscountsMap} />
      </Box>
    </RoundedBox>
  )
}
